import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Copy, Link2, Plus, Trash2 } from "lucide-react";
import client from "../api/client";
import BoardCanvas from "./BoardCanvas";
import Modal from "./Modal";
import TemplatePicker, { AspectPicker } from "./TemplatePicker";
import { buildTemplate } from "./templates";
import "./Moodboard.css";

const relative = (value) => {
  if (!value) return "";
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  if (seconds < 86400 * 30) return `${Math.round(seconds / 86400)} d ago`;
  return new Date(value).toLocaleDateString();
};

// Small cards only need the gallery thumbnails, not the 1400 px board previews.
const cardAssets = (assets = {}) =>
  Object.fromEntries(Object.entries(assets).map(([key, asset]) => [key, { ...asset, url: asset.thumb || asset.url }]));

function NewBoardDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [aspect, setAspect] = useState("landscape");
  const [template, setTemplate] = useState("hero");
  const [busy, setBusy] = useState(false);

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/api/moodboards", {
        title: title.trim() || "Untitled board",
        aspect,
        background: "#ffffff",
        items: buildTemplate(template, aspect),
      });
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not create the board.");
      setBusy(false);
    }
  };

  return (
    <Modal title="New mood board" onClose={onClose} wide>
      <form onSubmit={create} className="mb-new-board">
        <label className="mb-field">
          <span className="mb-field-label">Title</span>
          <input
            type="text"
            value={title}
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g. Living room concept"
            autoFocus
          />
        </label>
        <div className="mb-field">
          <span className="mb-field-label">Size</span>
          <AspectPicker value={aspect} onChange={setAspect} />
        </div>
        <div className="mb-field">
          <span className="mb-field-label">Layout (you can move and resize everything afterwards)</span>
          <TemplatePicker aspect={aspect} value={template} onChange={setTemplate} />
        </div>
        <div className="mb-modal-actions">
          <button type="button" className="mb-text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="mb-primary" disabled={busy}>
            {busy ? "Creating..." : "Create board"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function MoodboardList() {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/api/moodboards");
      setBoards(data.items);
      setLimit(data.limit);
      setError("");
    } catch (err) {
      console.error("Boards failed:", err);
      setError("Could not load your boards.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const duplicate = async (board) => {
    try {
      const { data } = await client.post(`/api/moodboards/${board.id}/duplicate`);
      setBoards((current) => [data, ...current]);
      toast.success("Board duplicated.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not duplicate the board.");
    }
  };

  const remove = async (board) => {
    if (!window.confirm(`Delete "${board.title}"? This cannot be undone.`)) return;
    try {
      await client.delete(`/api/moodboards/${board.id}`);
      setBoards((current) => current.filter((item) => item.id !== board.id));
    } catch {
      toast.error("Could not delete the board.");
    }
  };

  return (
    <main className="mb-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Studio</p>
          <h1>Mood Boards</h1>
        </div>
        <button type="button" className="mb-primary" onClick={() => setCreating(true)}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          New board
        </button>
      </header>

      {error && <p className="mb-error">{error}</p>}

      {loading ? (
        <p className="mb-muted">Loading boards...</p>
      ) : !boards.length && !error ? (
        <section className="mb-empty">
          <h2>Create your first mood board</h2>
          <p>
            Pick a layout, then fill it with artwork from the library or your own photos. Share a read-only link with a
            client or download it as a high-resolution PDF.
          </p>
          <button type="button" className="mb-primary" onClick={() => setCreating(true)}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            New board
          </button>
        </section>
      ) : (
        <>
          <section className="mb-board-grid">
            {boards.map((board) => (
              <article key={board.id} className="mb-board-card">
                <Link to={`/moodboards/${board.id}`} className="mb-board-card-preview" aria-label={`Open ${board.title}`}>
                  <BoardCanvas board={board} assets={cardAssets(board.assets)} compact fit />
                </Link>
                <div className="mb-board-card-meta">
                  <div>
                    <h2>
                      <Link to={`/moodboards/${board.id}`}>{board.title}</Link>
                    </h2>
                    <p>
                      Edited {relative(board.updated_at)}
                      {board.pages?.length > 1 && <span className="mb-board-count">{board.pages.length} boards</span>}
                      {board.share_token && (
                        <span className="mb-shared-tag">
                          <Link2 size={12} strokeWidth={2} aria-hidden="true" /> Shared
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="mb-card-actions">
                    <button type="button" className="mb-icon-button" onClick={() => duplicate(board)} title="Duplicate" aria-label={`Duplicate ${board.title}`}>
                      <Copy size={15} strokeWidth={1.8} />
                    </button>
                    <button type="button" className="mb-icon-button danger" onClick={() => remove(board)} title="Delete" aria-label={`Delete ${board.title}`}>
                      <Trash2 size={15} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </section>
          {limit != null && (
            <p className="mb-muted mb-count">
              {boards.length} of {limit} boards
            </p>
          )}
        </>
      )}

      {creating && (
        <NewBoardDialog onClose={() => setCreating(false)} onCreated={(board) => navigate(`/moodboards/${board.id}`)} />
      )}
    </main>
  );
}
